import { ATTACHMENT_LIMITS, AttachmentLimit } from "./enums";

export type AttachmentCategory = "image" | "video" | "audio" | "doc";

export type CategoryLimits = Partial<
  Record<AttachmentCategory, AttachmentLimit>
>;

const DOC_MIMETYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// Some Android pickers hand over a File with an empty `type`, so fall back to
// the extension before rejecting the file as an unsupported format.
const EXTENSION_CATEGORY: { [extension: string]: AttachmentCategory } = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  webp: "image",
  gif: "image",
  heic: "image",
  heif: "image",
  mp4: "video",
  mov: "video",
  m4v: "video",
  avi: "video",
  mkv: "video",
  webm: "video",
  "3gp": "video",
  mp3: "audio",
  wav: "audio",
  m4a: "audio",
  aac: "audio",
  ogg: "audio",
  pdf: "doc",
  doc: "doc",
  docx: "doc",
};

const CATEGORY_LABEL: Record<
  AttachmentCategory,
  { one: string; many: string }
> = {
  image: { one: "image", many: "photos" },
  video: { one: "video", many: "videos" },
  audio: { one: "audio file", many: "audio files" },
  doc: { one: "document", many: "documents" },
};

export const getAttachmentCategory = (
  file: File,
): AttachmentCategory | undefined => {
  const mimetype = file.type || "";

  if (mimetype.startsWith("image/")) return "image";
  if (mimetype.startsWith("video/")) return "video";
  if (mimetype.startsWith("audio/")) return "audio";
  if (DOC_MIMETYPES.includes(mimetype)) return "doc";

  const extension = file.name.split(".").pop()?.toLowerCase();
  return extension ? EXTENSION_CATEGORY[extension] : undefined;
};

export const formatSizeMB = (bytes: number) =>
  `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export interface AttachmentValidation {
  accepted: File[];
  error?: string;
}

export const validateAttachments = ({
  incoming,
  existing = [],
  limits = ATTACHMENT_LIMITS,
  maxTotalSizeMB,
  excludeFromTotal = [],
}: {
  incoming: File[];
  existing?: File[];
  limits?: CategoryLimits;
  maxTotalSizeMB?: number;
  /**
   * Categories that do not travel in the API request body — videos upload
   * straight to S3 — and so must not count towards the total-size cap.
   */
  excludeFromTotal?: AttachmentCategory[];
}): AttachmentValidation => {
  const accepted: File[] = [];
  const counts: Partial<Record<AttachmentCategory, number>> = {};

  existing.forEach((file) => {
    const category = getAttachmentCategory(file);
    if (category) counts[category] = (counts[category] ?? 0) + 1;
  });

  for (const file of incoming) {
    const category = getAttachmentCategory(file);

    if (!category || !limits[category]) {
      return {
        accepted: [],
        error: `"${file.name}" is not a supported file type. Please upload images, videos or PDF documents.`,
      };
    }

    const limit = limits[category]!;
    const label = CATEGORY_LABEL[category];

    // A category with an input ceiling is compressed in the browser before
    // upload, so at pick time only the raw ceiling applies — the upload cap is
    // checked against the compressed result afterwards.
    if (limit.max_input_size_mb) {
      if (file.size > limit.max_input_size_mb * 1024 * 1024) {
        return {
          accepted: [],
          error: `"${file.name}" is ${formatSizeMB(file.size)} — the largest ${
            label.one
          } we can compress is ${limit.max_input_size_mb} MB.`,
        };
      }
    } else if (file.size > limit.max_size_mb * 1024 * 1024) {
      return {
        accepted: [],
        error: `"${file.name}" is ${formatSizeMB(file.size)} — ${
          label.many
        } must be under ${limit.max_size_mb} MB.`,
      };
    }

    counts[category] = (counts[category] ?? 0) + 1;

    if (limit.max_count && counts[category]! > limit.max_count) {
      return {
        accepted: [],
        error: `Max ${limit.max_count} ${label.many} per complaint. Remove one before adding another.`,
      };
    }

    accepted.push(file);
  }

  if (maxTotalSizeMB) {
    const total = [...existing, ...accepted]
      .filter((file) => {
        const category = getAttachmentCategory(file);
        return !category || !excludeFromTotal.includes(category);
      })
      .reduce((sum, file) => sum + file.size, 0);

    if (total > maxTotalSizeMB * 1024 * 1024) {
      return {
        accepted: [],
        error: `These attachments add up to ${formatSizeMB(
          total,
        )}, over the ${maxTotalSizeMB} MB total limit. Please upload them in smaller batches.`,
      };
    }
  }

  return { accepted };
};

/**
 * Checks a file against the upload cap for its category, after any browser-side
 * compression has run. Returns an error message, or undefined when it fits.
 */
export const checkCompressedSize = (
  file: File,
  limits: CategoryLimits = ATTACHMENT_LIMITS,
): string | undefined => {
  const category = getAttachmentCategory(file);
  if (!category) return undefined;

  const limit = limits[category];
  if (!limit || file.size <= limit.max_size_mb * 1024 * 1024) return undefined;

  return `"${file.name}" is ${formatSizeMB(file.size)}, over the ${
    limit.max_size_mb
  } MB limit.`;
};

/** Human-readable hint line for a drop zone, e.g. "Images up to 10 MB". */
export const describeLimits = (limits: CategoryLimits) =>
  (Object.keys(limits) as AttachmentCategory[])
    .map((category) => {
      const limit = limits[category]!;
      const label = CATEGORY_LABEL[category];
      const count = limit.max_count ? `, max ${limit.max_count}` : "";

      // Videos are compressed in the browser, so advertise the raw ceiling and
      // make it clear what they are compressed down to.
      if (limit.max_input_size_mb)
        return `${label.many} up to ${limit.max_input_size_mb} MB${count} (compressed to under ${limit.max_size_mb} MB)`;

      return `${label.many} up to ${limit.max_size_mb} MB${count}`;
    })
    .join(" · ");
